import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Clock, 
  TrendingUp,
  Calendar,
  ChevronRight,
  Loader2
} from 'lucide-react';
import { useTimesheetForScreen } from '@/contexts/TimesheetContext';
import { useDataSource } from '@/contexts/DataSourceContext';
import { useOrganisationSettings } from '@/hooks/useOrganisationSettings';
import { useDashboardSummaryContext } from '@/contexts/DashboardSummaryContext';
import { format, subDays, startOfMonth, startOfQuarter, startOfYear } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { timesheetApi } from '@/services/timesheetApi';
import type { Project } from '@/types';

interface TopProjectsCardProps {
  onNavigateToProject?: (projectId: string) => void;
  maxProjects?: number;
  defaultTimePeriod?: TimePeriod;
  showActiveOnly?: boolean;
}

type TimePeriod = 'last30days' | 'last90days' | 'currentMonth' | 'currentQuarter' | 'yearToDate' | 'allTime';

interface ProjectTimeData {
  projectId: string;
  projectName: string;
  projectCode: string;
  totalHours: number;
  percentage: number;
  isActive: boolean;
  billable: boolean;
}

export function TopProjectsCard({ 
  onNavigateToProject,
  maxProjects = 5,
  defaultTimePeriod = 'last30days',
  showActiveOnly = false
}: TopProjectsCardProps) {
  const navigate = useNavigate();
  const { timesheets, currentEmployee, projects: contextProjects } = useTimesheetForScreen('homepage');
  const { getScreenMode } = useDataSource();
  const screenMode = getScreenMode('homepage');
  const { summary } = useDashboardSummaryContext();
  const { settings: orgSettings } = useOrganisationSettings('homepage');
  const [timePeriod, setTimePeriod] = useState<TimePeriod>(defaultTimePeriod);
  const [lastUpdated] = useState(new Date());
  
  // Load projects independently (for live mode)
  const [loadedProjects, setLoadedProjects] = useState<Project[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const requestedIdsRef = useRef<Set<string>>(new Set());
  
  // Fetch projects needed for the timesheet entries
  const fetchProjectsForEntries = useCallback(async (projectIds: string[]) => {
    if (screenMode === 'demo' || projectIds.length === 0) return;
    
    const idsToFetch = projectIds.filter(id => !requestedIdsRef.current.has(id));
    if (idsToFetch.length === 0) return;
    
    idsToFetch.forEach(id => requestedIdsRef.current.add(id));
    
    setIsLoadingProjects(true);
    try {
      const fetchedProjects = await timesheetApi.getProjectsByIds(idsToFetch);
      if (fetchedProjects.length > 0) {
        setLoadedProjects(prev => {
          const existingIds = new Set(prev.map(p => String(p.id)));
          const newProjects = fetchedProjects.filter(p => !existingIds.has(String(p.id)));
          return newProjects.length > 0 ? [...prev, ...newProjects] : prev;
        });
      }
    } catch (error) {
      console.error('Failed to fetch projects for TopProjectsCard:', error);
      idsToFetch.forEach(id => requestedIdsRef.current.delete(id));
    } finally {
      setIsLoadingProjects(false);
    }
  }, [screenMode]);
  
  // Get unique project IDs from timesheets
  const projectIdsFromTimesheets = useMemo(() => {
    const ids = new Set<string>();
    timesheets.forEach(ts => {
      if (ts.employeeId === currentEmployee.id) {
        ts.entries.forEach(entry => {
          if (entry.projectId) {
            ids.add(String(entry.projectId));
          }
        });
      }
    });
    return Array.from(ids);
  }, [timesheets, currentEmployee.id]);
  
  // Fetch projects when timesheet project IDs change
  useEffect(() => {
    if (screenMode === 'live' && projectIdsFromTimesheets.length > 0) {
      fetchProjectsForEntries(projectIdsFromTimesheets);
    }
  }, [projectIdsFromTimesheets, screenMode, fetchProjectsForEntries]);
  
  // Use context projects in demo mode, loaded projects in live mode
  const projects = useMemo(() => {
    if (screenMode === 'demo') {
      return contextProjects;
    }
    return loadedProjects;
  }, [screenMode, contextProjects, loadedProjects]);

  const projectTimeData = useMemo(() => {
    // Use backend data in live mode when available and using last30days period
    if (screenMode === 'live' && summary?.topProjects && timePeriod === 'last30days') {
      const backendProjects = summary.topProjects.projects
        .filter(data => !showActiveOnly || data.isActive)
        .slice(0, maxProjects);

      return {
        projects: backendProjects,
        totalHours: summary.topProjects.totalHours,
        hasData: backendProjects.length > 0
      };
    }

    // Fallback to client-side calculation for demo mode or non-default time periods
    const now = new Date();
    let startDate: Date;

    switch (timePeriod) {
      case 'last30days':
        startDate = subDays(now, 30);
        break;
      case 'last90days':
        startDate = subDays(now, 90);
        break;
      case 'currentMonth':
        startDate = startOfMonth(now);
        break;
      case 'currentQuarter':
        startDate = startOfQuarter(now);
        break;
      case 'yearToDate':
        startDate = startOfYear(now);
        break;
      case 'allTime':
        startDate = new Date(0);
        break;
      default:
        startDate = subDays(now, 30);
    }

    // Calculate the week start from weekEnding (weekEnding is Sunday, week starts Monday = weekEnding - 6 days)
    const isCalendarPeriod = ['currentMonth', 'currentQuarter', 'yearToDate'].includes(timePeriod);
    const userTimesheets = timesheets.filter(ts => {
      if (ts.employeeId !== currentEmployee.id || ts.status !== 'approved') return false;

      // The week covers weekEnding-6 .. weekEnding
      const weekStart = subDays(ts.weekEnding, 6);

      // Include if the week overlaps with [startDate, endDate]
      // For calendar periods, endDate is now (we still want current week included)
      // For rolling periods (last30days, last90days), keep the original weekEnding <= now check
      if (isCalendarPeriod) {
        // Week overlaps with [startDate, now]: weekStart <= now AND weekEnding >= startDate
        return weekStart <= now && ts.weekEnding >= startDate;
      } else if (timePeriod === 'allTime') {
        // allTime: include the current week as well
        return weekStart <= now;
      } else {
        // Original logic for rolling periods
        return ts.weekEnding >= startDate && ts.weekEnding <= now;
      }
    });

    const projectHoursMap = new Map<string, number>();

    userTimesheets.forEach(timesheet => {
      timesheet.entries.forEach(entry => {
        const totalHours = Object.values(entry.hours).reduce((sum, hours) => sum + hours, 0);
        const currentTotal = projectHoursMap.get(entry.projectId) || 0;
        projectHoursMap.set(entry.projectId, currentTotal + totalHours);
      });
    });

    const totalHours = Array.from(projectHoursMap.values()).reduce((sum, hours) => sum + hours, 0);

    const summaryProjects = summary?.topProjects?.projects;

    const projectData: ProjectTimeData[] = Array.from(projectHoursMap.entries())
      .map(([projectId, hours]) => {
        const project = projects.find(p => p.id === projectId);
        const summaryProject = summaryProjects?.find(sp => sp.projectId === projectId);
        return {
          projectId,
          projectName: project?.name || summaryProject?.projectName || 'Unknown Project',
          projectCode: project?.code || summaryProject?.projectCode || 'N/A',
          totalHours: hours,
          percentage: totalHours > 0 ? Math.round((hours / totalHours) * 100) : 0,
          isActive: project?.active ?? summaryProject?.isActive ?? false,
          billable: project?.billable ?? summaryProject?.billable ?? false
        };
      })
      .filter(data => !showActiveOnly || data.isActive)
      .sort((a, b) => b.totalHours - a.totalHours)
      .slice(0, maxProjects);

    return {
      projects: projectData,
      totalHours,
      hasData: projectData.length > 0
    };
  }, [screenMode, summary, timesheets, currentEmployee.id, projects, timePeriod, maxProjects, showActiveOnly]);

  const timePeriodLabels: Record<TimePeriod, string> = {
    last30days: 'Last 30 Days',
    last90days: 'Last 90 Days',
    currentMonth: 'Current Month',
    currentQuarter: 'Current Quarter',
    yearToDate: 'Year to Date',
    allTime: 'All Time'
  };

  const hoursPerDay = useMemo(() => {
    // In live mode, prefer the backend-computed hoursPerDay (uses employee's working pattern)
    if (screenMode === 'live' && summary?.topProjects?.hoursPerDay) {
      return summary.topProjects.hoursPerDay;
    }

    // Fallback: prefer employee's own working pattern, then org settings
    const pattern = currentEmployee?.workingPattern;
    if (pattern) {
      const hoursPerWeek = pattern.hoursPerWeek ?? pattern.standardHoursPerWeek;
      if (hoursPerWeek) {
        const workingDayCount = Object.values(pattern.workingDays)
          .filter(day => typeof day === 'boolean' ? day : day.isWorkingDay).length;
        if (workingDayCount > 0) {
          return hoursPerWeek / workingDayCount;
        }
      }
    }

    const workingProfile = orgSettings?.workingProfile;
    if (!workingProfile?.workingDays) return 8;
    const workingDayCount = Object.values(workingProfile.workingDays)
      .filter(day => day.isWorkingDay).length;
    if (workingDayCount === 0) return 8;
    return workingProfile.standardHoursPerWeek / workingDayCount;
  }, [screenMode, summary?.topProjects?.hoursPerDay, currentEmployee?.workingPattern, orgSettings?.workingProfile]);

  const formatHours = (hours: number): string => {
    const hpd = hoursPerDay || 8;
    if (hours >= hpd) {
      const days = Math.floor(hours / hpd);
      const remainingHours = hours % hpd;
      if (remainingHours === 0) {
        return `${days}d`;
      }
      return `${days}d ${remainingHours.toFixed(1)}h`;
    }
    return `${hours.toFixed(1)}h`;
  };

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Top Projects by Time
            </CardTitle>
            <CardDescription className="mt-1.5">
              Your personal time investment ranked by hours
            </CardDescription>
          </div>
          <Select value={timePeriod} onValueChange={(value) => setTimePeriod(value as TimePeriod)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last30days">Last 30 Days</SelectItem>
              <SelectItem value="last90days">Last 90 Days</SelectItem>
              <SelectItem value="currentMonth">Current Month</SelectItem>
              <SelectItem value="currentQuarter">Current Quarter</SelectItem>
              <SelectItem value="yearToDate">Year to Date</SelectItem>
              <SelectItem value="allTime">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {isLoadingProjects && screenMode === 'live' ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Loader2 className="h-12 w-12 text-muted-foreground/50 mb-3 animate-spin" />
            <p className="text-sm text-muted-foreground">
              Loading project data...
            </p>
          </div>
        ) : !projectTimeData.hasData ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Clock className="h-12 w-12 text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground">
              No approved time entries found for {timePeriodLabels[timePeriod].toLowerCase()}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Start logging time to see your top projects
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Total Hours Summary */}
            <div className="flex items-center justify-between p-3 bg-accent/50 rounded-lg">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Total Time Logged</span>
              </div>
              <span className="text-lg font-bold">{formatHours(projectTimeData.totalHours)}</span>
            </div>

            {/* Project List */}
            <div className="space-y-3">
              {projectTimeData.projects.map((project, index) => (
                <div
                  key={project.projectId}
                  className="group relative"
                >
                  {/* Project Header */}
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-muted-foreground/60 w-6">
                          #{index + 1}
                        </span>
                        <button
                          onClick={() => {
                            // Navigate to Active Projects page with project filter for single project
                            navigate(`/projects/active?projectId=${encodeURIComponent(project.projectId)}`);
                          }}
                          className="text-sm font-semibold truncate text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary/60 transition-all text-left cursor-pointer"
                          title={`View ${project.projectName} (${project.projectCode}) details`}
                        >
                          {project.projectName} - {project.projectCode}
                        </button>
                      </div>
                      <div className="flex items-center gap-2 mt-1 ml-8">
                        {project.billable && (
                          <Badge variant="secondary" className="text-xs px-1.5 py-0">
                            Billable
                          </Badge>
                        )}
                        {!project.isActive && (
                          <Badge variant="outline" className="text-xs px-1.5 py-0">
                            Inactive
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-lg font-bold">
                        {formatHours(project.totalHours)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {project.percentage.toFixed(0)}%
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="ml-8">
                    <div className="h-2 bg-accent rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-primary to-primary/80 transition-all duration-500 ease-out"
                        style={{ width: `${project.percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-3 border-t">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Calendar className="h-3 w-3" />
                <span>Updated {format(lastUpdated, 'MMM d, yyyy h:mm a')}</span>
              </div>
              {projectTimeData.projects.length >= maxProjects && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-auto py-1 px-2"
                  onClick={() => onNavigateToProject?.('all')}
                >
                  View All Projects
                  <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
